<template>
  <v-alert
    v-if="invitation"
    class="flex-none"
    type="info"
    variant="tonal"
    data-testid="account-invitation"
  >
    <div class="font-bold">
      {{ t("account.invitation.title", { supervisorOrgName: invitation.supervisorOrgName }) }}
    </div>
    <div class="text-sm">{{ t("account.invitation.message") }}</div>
    <div class="flex gap-2 mt-3">
      <v-btn
        color="primary"
        variant="flat"
        size="small"
        append-icon="mdi-open-in-new"
        :disabled="refusing"
        data-testid="account-invitation-sign-button"
        @click="openInNewTab('contractSigning', { accountId, invitationId: invitation.id })"
        >{{ t("account.invitation.sign") }}</v-btn
      >
      <v-btn
        variant="outlined"
        size="small"
        :disabled="refusing"
        data-testid="account-invitation-refuse-button"
        @click="confirmingRefusal = true"
        >{{ t("account.invitation.refuse") }}</v-btn
      >
    </div>
    <v-dialog v-model="confirmingRefusal" max-width="500" :persistent="refusing">
      <v-card>
        <v-card-title>{{ t("account.invitation.refuse") }}</v-card-title>
        <v-card-text>{{
          t("account.invitation.refuseConfirm", { supervisorOrgName: invitation.supervisorOrgName })
        }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="refusing" @click="confirmingRefusal = false">{{
            t("common.close")
          }}</v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="refusing"
            :disabled="refusing"
            data-testid="account-invitation-refuse-confirm"
            @click="refuse(invitation.id)"
            >{{ t("account.invitation.refuse") }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-alert>
</template>

<script lang="ts" setup>
import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { useOpenInNewTab } from "@/composables/useOpenInNewTab";
import { call, db } from "@/firebase";
import { useToast } from "@/store";
import {
  accountInvitationsFullPath,
  type AccountInvitation,
} from "@aircap/common/accountInvitation";
import { captureException } from "@sentry/vue";
import { collection, query, where } from "firebase/firestore";
import { computed, ref } from "vue";

const props = defineProps<{
  accountId: string;
}>();

const { t } = useI18n();
const { showErrorToast } = useToast();
const openInNewTab = useOpenInNewTab();

const { data: invitations } = useCollection<AccountInvitation>(
  computed(() =>
    query(
      collection<AccountInvitation>(db, accountInvitationsFullPath),
      where<AccountInvitation>("accountId", "==", props.accountId),
      where<AccountInvitation>("status", "==", "pending"),
    ),
  ),
);
const invitation = computed(() => invitations.value[0]);

const confirmingRefusal = ref(false);
const refusing = ref(false);

const refuse = async (invitationId: string) => {
  refusing.value = true;
  try {
    await call("refuseAccountInvitation")({ accountId: props.accountId, invitationId });
    confirmingRefusal.value = false;
  } catch (cause) {
    const error = new Error(`Error refusing invitation ${invitationId}`, { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    refusing.value = false;
  }
};
</script>
